<template>
  <div class="SmsList">
    <LoadingLayout v-if="loading"/>
    <Page
      v-else
      :title="type === 'template' ? $t('sms.templateMessages') : $t('sms.manualMessages')"
      :primaryAction="{
        label: $t('sms.newSms'),
        onAction: () => { showCreate = true }
      }"
      :secondaryActions="[{
        label: $t('Notes'),
        onAction: () => eventHub.$emit('showAccountNotes', 'SMS')
      }]"
      size="fullWidth"
    >
      <Modal
        :title="$t('sms.createSms')"
        :visible="showCreate"
        :primaryAction="{
          label: $t('Create'),
          onAction: createSms,
          disabled: nameError || (!newMessage.siteId && type === 'template') || !newMessage.name
        }"
        @close="closeCreate"
        overflowVisible
      >
        <FormLayout>
          <TextField
            :label="$t('Name')"
            :placeholder="$t('sms.newSms')"
            :error="nameError"
            v-model="newMessage.name"
          />
          <Select
            v-if="type === 'template'"
            :label="$t('Domain')"
            :placeholder="$t('sms.selectDomain')"
            :options="domainOptions"
            v-model="newMessage.siteId"
          />
        </FormLayout>
      </Modal>
      <Layout>
        <LayoutSection>
          <Card>
            <Tabs
              :tabs="getTabs()"
              v-model="status"
            />
            <Filters
              :query.sync="filter"
            />
            <DataTable
              :columns="columns"
              :rows="currentMessages"
              :row-actions="(m) => getRowActions(m)"
              :emptyText="$t('sms.noMessages')"
            > 
              <template v-slot:col:name="{row}">
                <PageLink :href="getEditMessageUrl(row)">{{row.name}}</PageLink>
              </template>
            </DataTable>
          </Card>

        </LayoutSection>
      </Layout>
      <!-- <pre>{{store.languages}}</pre> -->

    </Page>
  </div>
</template>

<script>

// import moment from 'moment'

export default {
  name: 'SmsList',
  inject: ['store'],
  data() {
    return {
      filter: '',
      status: 'draft',
      type: 'manual',
      loading: false,
      messages: [],
      showCreate: false,
      newMessage: {
        name: '',
        siteId: null
      },
      lastLoaded: null,
      siteSettings: []
    };
  },
  mounted() {
    this.loadMessages()
    // make sure we relead notificaitons are at least 30 seconds fresh on change ...
    this.$watch('status', () => {
      if (this.lastLoaded && this.lastLoaded < Date.now() - (30 * 1000)) {
        this.loadMessages()
      }
    })
    this.$watch('type', () => {
      this.loadMessages()
    })
    this.$watch('$route', (to) => {
      this.status = to.params.status
      this.type = to.params.type
    })
    this.status = this.$route.params.status
    this.type = this.$route.params.type

    this.$watch('status', (newStatus) => {
      this.$router.push(`/messaging/sms/list/type/${this.$route.params.type}/status/${newStatus}/`)
    })
  },
  computed: {
    currentMessages() {
      let messages = this.messages.filter( o => o.type === this.type)
      messages = messages.filter((o) => {
        switch (this.status) {
          case 'draft':
            if (o.status === 'draft') return true
            break;
          case 'sent':
            if (o.status === 'scheduled-lock' || o.status === 'sent') return true
            break;
          case 'active':
            if (o.status === 'active') return true
            break;
          case 'paused':
            if (o.status === 'paused') return true
            break;
          case 'errored':
            if (o.status === 'errored') return true
            break;
          case 'scheduled':
            if (o.status === 'scheduled') return true
            break;
          default:
            return false
        }
        return false
      })
      const filter = this.filter.toLowerCase().trim()
      if (filter) {
        messages = messages.filter(o => o.name.toLowerCase().indexOf(filter) > -1)
      }
      // added this map for datatable
      return messages.map(m => {
        const site = this.siteSettings.find(d => d.siteId === m.siteId)
        const language = site && this.store.languages.find(l => l.value === site.language)
        return {
          ...m,
          domain: site && site.domain,
          language: language && language.label,
          updated: m.sentOn || m.updatedOn || m.createdOn
        }
      })
    },
    numberOfDrafts() {
      return this.messages.filter(o => o.status === 'draft' && (o.type === this.type)).length
    },
    numberOfScheduled() {
      return this.messages.filter(o => o.status === 'scheduled' && (o.type === this.type)).length
    },
    numberOfSent() {
      return this.messages.filter(o => (o.status === 'scheduled-lock' || o.status === 'sent') && (o.type === this.type)).length
    },
    numberOfErrored() {
      return this.messages.filter(o => (o.status === 'errored')).length
    },
    numberOfActive() {
      return this.messages.filter(o => o.status === 'active' && (o.type === this.type)).length
    },
    numberOfPaused() {
      return this.messages.filter(o => o.status === 'paused' && (o.type === this.type)).length
    },
    columns() {
      const rv = [{
          key: 'name',
          label: this.$t('Name'),
          fixed: true
        },
        {
          key: 'domain',
          label: this.$t('Domain')
        },
        {
          key: 'language',
          label: this.$t('Language')
        },
        {
          key: 'updated',
          label: this.$t('Updated'),
          format: 'dateShort'
        },
        {
          key: 'createdByName',
          label: this.$t('By')
        },
      ]
      return rv
    },
    domainOptions() {
      return this.siteSettings.map(d => {
        return {
          label: d.domain,
          value: d.siteId
        }
      })
    },
    nameError() {
      return this.newMessage.name && this.messages.find(o => o.name.toLowerCase() === this.newMessage.name.toLowerCase().trim()) ? 'SMS Name Already Exists' : ''
    }
  },
  methods: {
    getTabs() {
      if (this.type === 'manual') {
        return [
          { id: 'draft', label: this.$t('Draft'), badge: `${this.numberOfDrafts}` },
          { id: 'scheduled', label: this.$t('Scheduled'), badge: `${this.numberOfScheduled}` },
          { id: 'sent', label: this.$t('Sent'), badge: `${this.numberOfSent}` },
          { id: 'errored', label: this.$t('Errored'), badge: `${this.numberOfErrored}` },
        ]
      } else {
        return [
          { id: 'draft', label: this.$t('Draft'), badge: `${this.numberOfDrafts}` },
          { id: 'active', label: this.$t('Active'), badge: `${this.numberOfActive}` },
          { id: 'paused', label: this.$t('Paused'), badge: `${this.numberOfPaused}` },
        ]
      }
    },
    getEditMessageUrl(message) {
      return this.$route.params.type === 'template'
        ? `#/messaging/sms/edit/id/${message.smsId}/page/details/`
        : `#/messaging/sms/edit/id/${message.smsId}/page/filters/`
    },
    getRowActions(message) {
      const rv = []
      if (this.status === 'active' || this.status === 'draft') {
        rv.push(...[
          {
            label: this.$t('Copy'),
            onAction: () => (this.copySmsMessage(message.smsId, message.name))
          },
          {
            label: this.$t('Delete'),
            onAction: () => (this.deleteMessage(message.smsId))
          }
        ])
      }
      if (this.status === 'active') {
        rv.push({
          label: this.$t('Pause'),
          onAction: () => (this.pauseMessage(message.smsId))
        })
      }
      if (this.status === 'paused') {
        rv.push({
          label: this.$t('Activate'),
          onAction: () => (this.activateMessage(message.smsId))
        })
      }
      if (this.status === 'scheduled' || this.status === 'sent') {
        rv.push(...[
          {
            label: this.$t('Copy'),
            onAction: () => (this.copySmsMessage(message.smsId))
          }
        ])
      }
      if (this.status === 'errored') {
        rv.push({
          label: this.$t('Make Draft'),
          onAction: () => (this.makeDraftFromErrored(message.smsId))
        })
      } else {
        rv.push({
          label: this.$t('Rename'),
          onAction: () => (this.renameMessage(message))
        })
      }
      if (this.status === 'scheduled') {
        rv.push({
          label: this.$t('Cancel'),
          onAction: () => (this.cancelMessage(message))
        })
      }
      return rv
    },
    loadMessages() {
      this.lastLoaded = Date.now()
      NProgress.start()
      this.loading = true
      Promise.all([this.api.getSmsMessages(), this.api.getSmsSiteSettings()])
        .then((data) => {
          const [messages, siteSettings] = data
          this.messages = messages
          this.siteSettings = siteSettings
        })
        .catch(this.apiCatch)
        .finally(() => {
          this.loading = false
          NProgress.done()
        })
    },
    closeCreate() {
      this.showCreate = false
      this.newMessage = {
        name: '',
        siteId: null
      }
    },
    createSms() {
      NProgress.start()
      this.showCreate = false
      const type = this.$route.params.type
      this.api.createSms(this.newMessage.name.trim(), this.newMessage.siteId, type)
        .then((data) => {
          this.$ui.toast({ title: this.$t('Created') })
          this.messages.push(data)
          if (type === 'template') {
            this.$router.push(`/messaging/sms/edit/id/${data.smsId}/page/details/`)
          } else {
            this.$router.push(`/messaging/sms/edit/id/${data.smsId}/`)
          }
        })
        .catch(this.apiCatch)
        .finally(() => {
          this.closeCreate()
          NProgress.done()
        })
    },
    renameMessage(message) {
      const self = this
      this.$ui.prompt({
        title: self.$t('sms.renameMessage'),
        inputPlaceholder: self.$t('sms.smsName'),
        inputValue: message.name,
        inputValidator(name) {
          if (self.messages.find(o => o.name.toLowerCase() === name.toLowerCase().trim())) {
            return self.$t('sms.nameExistsError')
          }
          return true
        },
        onSubmit(newName) {
          NProgress.start()
          self.api.getSmsMessage(message.smsId)
            .then((data) => {
              const messageToUpdate = data
              messageToUpdate.name = newName.trim()
              return self.api.updateSmsMessage(messageToUpdate)
            })
            .then((data) => {
              const updatedMessage = data
              const ind = self.messages.findIndex(m => m.smsId === updatedMessage.smsId)
              self.$set(self.messages, ind, updatedMessage)
              self.$ui.toast({ title: self.$t('Saved') })
            })
            .catch(self.apiCatch)
            .finally(() => {
              NProgress.done()
            })
        }
      });
    },
    deleteMessage(id) {
      const self = this
      this.$ui.confirm({
        title: self.$t('sms.askDeleteMessage', {value: self.status}),
        onConfirm() {
          NProgress.start()
          self.api.deleteSmsMessage(id)
            .then(() => {
              self.messages = self.messages.filter(m => m.smsId !== id)
              self.$ui.toast({ title: self.$t('Saved') })
            })
            .catch(self.apiCatch)
            .finally(() => {
              NProgress.done()
            })
        }
      })
    },
    pauseMessage(id) {
      NProgress.start()
      this.api.pauseSmsMessage(id)
        .then((data) => {
          const updatedMessage = data
          const ind = this.messages.findIndex(m => m.smsId === updatedMessage.smsId)
          this.$set(this.messages, ind, updatedMessage)
          this.$ui.toast({ title: this.$t('Saved') })
        })
        .catch(this.apiCatch)
        .finally(() => {
          NProgress.done()
        })
    },
    activateMessage(id) {
      NProgress.start()
      this.api.activateSmsMessage(id)
        .then((data) => {
          const updatedMessage = data
          const ind = this.messages.findIndex(m => m.smsId === updatedMessage.smsId)
          this.$set(this.messages, ind, updatedMessage)
          this.$ui.toast({ title: this.$t('Saved') })
        })
        .catch(this.apiCatch)
        .finally(() => {
          NProgress.done()
        })
    },
    cancelMessage(message) {
      const self = this
      this.$ui.confirm({
        title: self.$t('sms.cancelMessage'),
        onConfirm() {
          NProgress.start()
          self.api.getSmsMessage(message.smsId)
            .then((data) => {
              const messageToUpdate = data
              messageToUpdate.status = 'draft'
              return self.api.updateSmsMessage(messageToUpdate)
            })
            .then((data) => {
              const updatedMessage = data
              const ind = self.messages.findIndex(m => m.smsId === updatedMessage.smsId)
              self.$set(self.messages, ind, updatedMessage)
              self.$ui.toast({ title: self.$t('Saved') })
            })
            .catch(self.apiCatch)
            .finally(() => {
              NProgress.done()
            })
        }
      });
    },
    makeDraftFromErrored(id) {
      NProgress.start()
      this.api.getSmsMessage(id)
        .then((data) => {
          const message = data
          message.status = 'draft'
          return this.api.updateSmsMessage(message)
        })
        .then((data) => {
          const updatedMessage = data
          const index = this.messages.findIndex(n => n.smsId === updatedMessage.smsId)
          if (index !== -1) {
            this.messages.splice(index, 1)
            this.messages.unshift(updatedMessage)
          }
          this.$router.push(`/messaging/sms/list/type/${this.$route.params.type}/status/draft/`)
            .catch(this.duplicateRouteCatch)
          this.$ui.toast({ title: this.$t('Saved') })
        })
        .catch(this.apiCatch)
        .finally(() => {
          NProgress.done()
        })
    },
    copySmsMessage(id, oldName) {
      const self = this
      this.$ui.prompt({
        title: self.$t('sms.copySmsMessage'),
        content: self.$t('sms.copyDateChangeMessage'),
        inputPlaceholder: self.$t('sms.smsName'),
        inputValue: oldName,
        inputValidator(name) {
          if (self.messages.find(o => o.name.toLowerCase() === name.toLowerCase().trim())) {
            return self.$t('sms.nameExistsError')
          }
          return true
        },
        onSubmit(newName) {
          NProgress.start()
          self.api.copySmsMessage(id, newName.trim())
            .then((data) => {
              self.messages.unshift(data)
              self.$router.push(`/messaging/sms/list/type/${self.$route.params.type}/status/draft/`)
                .catch(self.duplicateRouteCatch)
              self.$ui.toast({ title: self.$t('Saved') })
            })
            .catch(self.apiCatch)
            .finally(() => {
              NProgress.done()
            })
        }
      });
    }
  }
};
</script>

<style lang="less" scoped>
  #filter {
    display: inline-block;
    margin-right: 15px;
  }
  .btn-default .badge {
    background: #aaa;
  }
  span.name {
    display: block;
    font-size: 13px;
  }
  .no-noticitaions {
    margin: 15px;
  }
  .update-info-date {
    font-size: 80%;
    color: #666;
  }
  .section-header {
    display: flex;
  }
  .section-header-actions {
    margin-left: auto;
  }
  #filter {
    float: left;
  }
  .filter-buttons {
    float: left;
  }
</style>

